import React, { useEffect, useMemo } from 'react';

import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess } from 'Deer/helpers/getUserAccess';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  OneOnOneIntentType,
  setSelectedOneOnOneIntent,
} from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';
import { ONE_ON_ONE_FILTERS_ARRAY } from 'screens/oneOnOneRabbit/landingPage/features/LandingPageOneOnOne/ManagePageOneOnOne';
import { checkVisierAnalyticsEnabled } from 'utils/helpers/featureFlags';
import { checkOneOnOneModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsOneOnOneAdmin } from 'utils/helpers/rolesAccess';

import { ONE_ON_ONE_INTENTS } from 'constants/moduleIntentTypes';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { useOneOnOneEllipsisValues } from '../hooks/useOneOnOneEllipsisValues';
import { OneOnOneMetaFilters } from './OneOnOneMetaFilters';

type Props = {
  showKebabMenu?: boolean;
  gridRef?: any;
  breadcrumbs: IntentBreadcrumbType;
};

const OneOnOneIntentDropdown = ({ showKebabMenu = true, gridRef, breadcrumbs }: Props) => {
  const { l1Module } = useRouteToNavLevels();
  const { teamType } = useRouteToMyTeamLevels();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const userAccess = getUserAccess();

  const ellipsisOptions = useOneOnOneEllipsisValues({ gridRef });

  const oneOnOneTypeFromQueryParam = new URLSearchParams(search).get('oneOnOneType');

  const isMyDataRoute = l1Module === 'myData';

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<OneOnOneIntentType>>(() => {
    return [
      {
        id: ONE_ON_ONE_INTENTS.ONE_ON_ONE_LIST,
        label: isMyDataRoute ? 'My 1:1s' : 'Team 1:1s',
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
            team: {
              directReports: L3_PATHS.team.directReports.oneOnOne.manage,
              allReports: L3_PATHS.team.allReports.oneOnOne.manage,
            },
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];

          history.push({
            pathname: urlToPush,
            search: `oneOnOneType=${ONE_ON_ONE_FILTERS_ARRAY[0].sectionId}`,
          });
        },
        visible: () => l1Module !== 'company',
        value: {
          myData: {
            path: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
            isAccessible: checkOneOnOneModuleEnabled,
          },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.oneOnOne.manage,
              isAccessible: checkOneOnOneModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.oneOnOne.manage,
              isAccessible: checkOneOnOneModuleEnabled,
            },
          },
        },
        // applicableGroupByValues,
        // showGroupByDropdown: selectedCohortId !== -12 && OKRManageView === 'list',
        showTimelineDropdown: true,
        showEllipsisMenu: false,
        queryParamValue: 'ACTIVE',
        category: 'Manage',
        hasMetaFilters: true,
      },
      {
        id: ONE_ON_ONE_INTENTS.ARCHIVED_ONE_ON_ONE,
        label: `Archived 1:1s`,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
            team: {
              directReports: L3_PATHS.team.directReports.oneOnOne.manage,
              allReports: L3_PATHS.team.allReports.oneOnOne.manage,
            },
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];
          history.push({
            pathname: urlToPush,
            search: `oneOnOneType=${ONE_ON_ONE_FILTERS_ARRAY[1].sectionId}`,
          });
        },
        visible: () => l1Module !== 'company',
        value: {
          myData: {
            path: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
            isAccessible: checkOneOnOneModuleEnabled,
          },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.oneOnOne.manage,
              isAccessible: checkOneOnOneModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.oneOnOne.manage,
              isAccessible: checkOneOnOneModuleEnabled,
            },
          },
        },
        // applicableGroupByValues,
        // showGroupByDropdown: true,
        showTimelineDropdown: true,
        showEllipsisMenu: false,
        queryParamValue: 'ARCHIVED',
        category: 'Manage',
        hasMetaFilters: true,
      },
      {
        id: ONE_ON_ONE_INTENTS.ONE_ON_ONE_GRAPH_REPORTS,
        label: l1Module === 'company' ? `Graph reports` : 'Reports',
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.oneOnOne.embeddedAnalytics,
              allReports: L3_PATHS.team.allReports.oneOnOne.embeddedAnalytics,
            },
            company: L3_PATHS.company.oneOnOne.embeddedAnalytics,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.directReports);
          history.push(urlToPush);
        },
        visible: () =>
          checkVisierAnalyticsEnabled() &&
          (l1Module === 'team' || l1Module === 'company') &&
          checkUserIsOneOnOneAdmin(),
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.oneOnOne.embeddedAnalytics,
              isAccessible: () => checkVisierAnalyticsEnabled() && checkOneOnOneModuleEnabled(),
            },
            allReports: {
              path: L3_PATHS.team.allReports.oneOnOne.embeddedAnalytics,
              isAccessible: () => checkVisierAnalyticsEnabled() && checkOneOnOneModuleEnabled(),
            },
          },
          company: {
            path: L3_PATHS.company.oneOnOne.embeddedAnalytics,
            isAccessible: () => checkVisierAnalyticsEnabled() && checkOneOnOneModuleEnabled(),
          },
        },
        showGroupByDropdown: false,
        showTimelineDropdown: false,
        showEllipsisMenu: false,
        category: 'Analyze',
        hasMetaFilters: false,
      },
      {
        id: ONE_ON_ONE_INTENTS.ONE_ON_ONE_TABLE_REPORTS,
        label: `Table reports`,
        onClick: () => {
          const values = {
            team: {
              allReports: L3_PATHS.team.allReports.oneOnOne.insights,
            },
            company: L3_PATHS.company.oneOnOne.insights,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.allReports);
          history.push(urlToPush);
        },
        visible: () =>
          (checkUserIsOneOnOneAdmin() || userAccess.isCustomOneOnOneAdmin) &&
          ((l1Module === 'team' && teamType === 'allReports') || l1Module === 'company'),
        value: {
          team: {
            allReports: {
              path: L3_PATHS.team.allReports.oneOnOne.insights,
              isAccessible: () =>
                (checkUserIsOneOnOneAdmin() || userAccess.isCustomOneOnOneAdmin) && checkOneOnOneModuleEnabled(),
            },
          },
          company: {
            path: L3_PATHS.company.oneOnOne.insights,
            isAccessible: () =>
              (checkUserIsOneOnOneAdmin() || userAccess.isCustomOneOnOneAdmin) && checkOneOnOneModuleEnabled(),
          },
        },
        showGroupByDropdown: true,
        showTimelineDropdown: true,
        showEllipsisMenu: true,
        category: 'Analyze',
        hasMetaFilters: true,
      },
      {
        id: ONE_ON_ONE_INTENTS.ONE_ON_ONE_SETTINGS,
        label: `Settings`,
        onClick: () => {
          history.push(L3_PATHS.company.oneOnOne.settings);
        },
        visible: () => checkUserIsOneOnOneAdmin() && l1Module === 'company',
        value: {
          company: {
            path: L3_PATHS.company.oneOnOne.settings,
            isAccessible: () => checkUserIsOneOnOneAdmin() && checkOneOnOneModuleEnabled(),
          },
        },
        showGroupByDropdown: false,
        showTimelineDropdown: false,
        showEllipsisMenu: false,
        category: 'Configure',
        hasMetaFilters: false,
      },
    ];
  }, [l1Module, teamType, history, userAccess.isCustomOneOnOneAdmin]);

  const selectedIntent = useMemo(
    () =>
      intentDropdownOptions.find((option) => {
        // for query param based intent options such as active and archived 1:1s
        if (option.queryParamValue) {
          return (
            oneOnOneTypeFromQueryParam === option.queryParamValue &&
            (pathname.includes(option.value.myData.path) || pathname.includes(option.value.team?.[teamType]?.path))
          );
        }
        const isTeamType = l1Module === 'team';
        if (isTeamType) {
          return pathname.includes(option.value.team?.[teamType]?.path);
        }
        return pathname.includes(option.value[l1Module]?.path);
      }),
    [intentDropdownOptions, l1Module, pathname, oneOnOneTypeFromQueryParam, teamType]
  );

  useEffect(() => {
    // if selectedIntent changes then update it in redux
    dispatch(setSelectedOneOnOneIntent(selectedIntent));
  }, [selectedIntent, dispatch]);

  return (
    <IntentFilters
      // @ts-expect-error options type error
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      KebabMenu={
        // @ts-expect-error options type error
        selectedIntent?.showEllipsisMenu ? <AgGridEllipsisMenu options={ellipsisOptions} /> : null
      }
      MetaFilters={<OneOnOneMetaFilters />}
      showKebabMenu={showKebabMenu}
      moduleName={meshModules.oneOnOne.value}
      intentTitle={meshModules.oneOnOne.label}
      isCategorized
      breadcrumbs={breadcrumbs}
      testIds={{
        dropdownContent: 'one-on-one-intent-dropdown-content',
        dropdownTrigger: 'one-on-one-intent-dropdown-trigger',
      }}
    />
  );
};

export { OneOnOneIntentDropdown };
